import { NOT_SHOWN_REASONS, type NotShownReason, type StoreDashboard } from '@avrahami/contracts';
import { Alert, Card, Loading, PageHeader } from '@avrahami/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { dashboardQuery } from '../api/tenant';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/forms';

function Stat({
  label,
  value,
  testId,
}: {
  label: string;
  value: string | number;
  testId?: string;
}) {
  return (
    <div className="rounded-md bg-slate-50 p-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-lg font-semibold text-slate-900" data-testid={testId}>
        {value}
      </p>
    </div>
  );
}

const notShownCount = {
  NO_COPY: (counts) => counts.noCopy,
  READY: (counts) => counts.ready,
} satisfies Record<NotShownReason, (counts: StoreDashboard['products']['notShown']) => number>;

/**
 * The products shoppers do not see, by the reason, each with a link to its list: a store owner
 * should never have to find out from a shopper that a product is missing.
 */
function NotShownAlert({ products }: { products: StoreDashboard['products'] }) {
  const total = products.draft + products.hidden;
  if (total === 0) return null;
  const reasons = NOT_SHOWN_REASONS.map((reason) => ({
    reason,
    count: notShownCount[reason](products.notShown),
  })).filter((entry) => entry.count > 0);
  return (
    <Alert tone="warning">
      <div data-testid="not-shown-alert">
        <p className="font-semibold">
          {total === 1
            ? he.dashboard.notShownOne
            : he.dashboard.notShownMany.replace('{count}', String(total))}
        </p>
        <p className="mt-1">{he.dashboard.notShownWhy}</p>
        <ul className="mt-1 list-disc ps-5">
          {reasons.map(({ reason, count }) => (
            <li key={reason} data-reason={reason}>
              <span className="font-semibold">{count}</span> · {he.notShownAction[reason]}{' '}
              <Link to="/products" search={{ notShown: reason }} className="underline">
                {he.dashboard.notShownLink}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </Alert>
  );
}

/** The store at a glance: quote requests to handle, the catalog's state, and the way to the store. */
export function DashboardPage() {
  const dashboard = useQuery(dashboardQuery());
  if (dashboard.isPending) return <Loading />;
  if (dashboard.isError) return <Alert>{errorMessage(dashboard.error)}</Alert>;
  const { store, orders, products, customers } = dashboard.data;

  return (
    <>
      <PageHeader
        title={store.name}
        actions={
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {store.url === null ? (
              <span className="text-sm text-slate-500">{he.dashboard.noUrl}</span>
            ) : (
              <a
                href={store.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="store-url"
                className="ltr text-sm font-medium text-brand-700 underline"
              >
                {he.dashboard.storeLink}: {store.url.replace(/^https?:\/\//, '')}
              </a>
            )}
          </div>
        }
      />
      <div className="flex flex-col gap-6">
        {store.opened ? null : <Alert tone="info">{he.dashboard.notOpened}</Alert>}
        {store.siteOpen ? null : (
          <Alert tone="warning">
            {he.dashboard.siteClosed}{' '}
            <Link to="/settings" className="underline">
              {he.dashboard.openSettings}
            </Link>
          </Alert>
        )}
        <NotShownAlert products={products} />
        <Card title={he.dashboard.orders}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Stat label={he.dashboard.newOrders} value={orders.new} testId="stat-new-orders" />
            <Stat label={he.dashboard.inProgress} value={orders.inProgress} />
            <Stat label={he.dashboard.today} value={orders.today} />
          </div>
          <Link
            to="/quote-requests"
            className="mt-3 inline-block text-sm font-medium text-brand-700 hover:underline"
            data-testid="dashboard-orders-link"
          >
            {he.dashboard.ordersLink}
          </Link>
        </Card>
        <Card title={he.dashboard.products}>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label={he.dashboard.activeProducts} value={products.active} />
            <Stat label={he.dashboard.draftProducts} value={products.draft} />
            <Stat label={he.dashboard.hiddenProducts} value={products.hidden} />
            <Stat label={he.dashboard.withoutCopy} value={products.withoutCopy} />
            <Stat label={he.dashboard.sourceDiscontinued} value={products.sourceDiscontinued} />
            <Stat label={he.dashboard.customers} value={customers.total} />
          </div>
          <p className="mt-3 text-xs text-slate-500" data-testid="dashboard-copy-note">
            {he.dashboard.copyNote}
          </p>
        </Card>
      </div>
    </>
  );
}
